import { MoneySlider } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { formatMoney } from '@hex/calculators-shared/utils';
import { Flex, View, Typography, tokens } from '@sapphire-react/core';
import { NumberField } from '@sapphire-react/lab';
import React, { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { STEP, MINIMUM_LOAN_VALUE } from '../../../constants';

interface LoanAdjustProps {
  minValue?: number;
  maxValue?: number;
  loanAmount: number;
  isDisabled?: boolean;
  setLoanAmount: (value: number) => void;
}

const LoanAdjust: React.FC<LoanAdjustProps> = ({
  minValue = 0,
  maxValue = 0,
  loanAmount,
  setLoanAmount,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { isExtraSmall, isSmall, isMedium } = useSapphireMedia();

  const handleLoanAmountSliderChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const newAmount = Number(event.target.value);

      if (!isNaN(newAmount) && newAmount !== loanAmount) {
        setLoanAmount(newAmount);
      }
    },
    [setLoanAmount, loanAmount],
  );

  const handleLoanAmountInputChange = useCallback(
    (value: number) => {
      if (value !== loanAmount) {
        setLoanAmount(value);
      }
    },
    [setLoanAmount, loanAmount],
  );

  return (
    <View paddingLeft={{ base: 0, md: tokens.size.spacingMd }} paddingRight={{ base: 0, md: tokens.size.spacingMd }}>
      <Typography.Body size={'md'} fontWeight={tokens.font.weightDefaultMedium}>
        {t('results.loanAdjust.heading')}
      </Typography.Body>
      <Typography.Body
        size={'sm'}
        marginTop={tokens.size.spacing4xs}
        UNSAFE_style={{ color: tokens.color.foregroundSecondary.value }}
      >
        {t('results.loanAdjust.subHeading')}
      </Typography.Body>
      <Flex
        alignItems={'center'}
        justifyContent={'space-between'}
        marginTop={tokens.size.spacingMd}
        position={'relative'}
      >
        <View width={{ base: '60%', sm: '55%', md: '60%' }} minWidth={140}>
          <MoneySlider
            min={minValue}
            max={maxValue}
            minLabel={formatMoney(minValue, { currency: '€' })}
            maxLabel={formatMoney(maxValue, { currency: '€' })}
            step={STEP}
            amount={loanAmount}
            handleSliderChange={handleLoanAmountSliderChange}
            isDisabled={isDisabled || maxValue <= MINIMUM_LOAN_VALUE}
            name={'loanAmount'}
          />
        </View>
        <View marginBottom={tokens.size.spacingSm}>
          <NumberField
            data-test-id={'loan-amount'}
            size={isExtraSmall || isSmall || isMedium ? 'md' : 'lg'}
            alignInputRight
            aria-label={t('results.loanAdjust.heading')}
            value={loanAmount}
            onChange={handleLoanAmountInputChange}
            minValue={minValue}
            maxValue={maxValue}
            affix={{
              postfix: '€',
            }}
            step={STEP}
            width={128}
            isDisabled={isDisabled || loanAmount <= MINIMUM_LOAN_VALUE}
          />
        </View>
      </Flex>
    </View>
  );
};

export default LoanAdjust;
